// ABOUTME: Hook for subscription card data processing.
// ABOUTME: Transforms raw subscription data into a structured format for UI components.

import { useMemo } from "react"
import { differenceInCalendarDays, startOfDay } from "date-fns"
import type {
  ExtendedSubscriptionItem,
  SubscriptionCardData,
  SubscriptionStatus,
} from "@/features/settings/components/Payment/types/subscription"
import type { ProductListBuyFragment, ProductType } from "@/graphql/main/gql/graphql"

interface UseSubscriptionCardDataProps {
  product: ProductListBuyFragment
  productType: ProductType
  isPaid: boolean
  datePaid?: string
  canceledSubscriptions: ExtendedSubscriptionItem[]
  isLoadingSubscriptionList: boolean
  onSubscribe: (product: ProductListBuyFragment) => void
  onSubscriptionCancel: (productType: ProductType) => void
  isLoadProductList: boolean
  isLoadBuy: boolean
  isLoadCancelSubscribe: boolean
}

export const useSubscriptionCardData = ({
  product,
  productType,
  isPaid,
  datePaid,
  canceledSubscriptions,
  isLoadingSubscriptionList,
  onSubscribe,
  onSubscriptionCancel,
  isLoadProductList,
  isLoadBuy,
  isLoadCancelSubscribe,
}: UseSubscriptionCardDataProps): SubscriptionCardData => {
  const status: SubscriptionStatus = useMemo(() => {
    const nextPaidDay = datePaid ? differenceInCalendarDays(startOfDay(new Date(datePaid)), startOfDay(new Date())) : 0

    // Показываем canceledStatus только после завершения загрузки данных о подписках
    // Это предотвращает мерцание при первоначальном рендере
    const canceledStatus = isLoadingSubscriptionList ? undefined : canceledSubscriptions.length === 0

    return {
      isPaid,
      datePaid,
      nextPaidDay,
      canceledStatus,
    }
  }, [isPaid, datePaid, isLoadingSubscriptionList, canceledSubscriptions])

  return useMemo(
    () => ({
      product: {
        product,
        productType,
      },
      status,
      actions: {
        onSubscribe,
        onSubscriptionCancel,
      },
      loading: {
        isLoadProductList,
        isLoadBuy,
        isLoadingSubscriptionList,
        isLoadCancelSubscribe,
      },
      canceledSubscriptions,
    }),
    [
      product,
      productType,
      status,
      onSubscribe,
      onSubscriptionCancel,
      isLoadProductList,
      isLoadBuy,
      isLoadingSubscriptionList,
      isLoadCancelSubscribe,
      canceledSubscriptions,
    ],
  )
}
